import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Observable } from 'rxjs/Observable';

import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse
} from '@angular/common/http';
import { AuthTokensService } from './auth-tokens.service';
import { AccountServiceProvider } from '@services/user/account.service';
import { AccessTokens } from '@models/models';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { MessageService } from '@services/helpers/message-service/message.service';

@Injectable()
export class WebRequestsInterceptor implements HttpInterceptor {
	constructor(
		private location: Location,
		private authTokensService: AuthTokensService,
		private accountService: AccountServiceProvider,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private messageService: MessageService
	) {}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		req = req.clone();

		const token = this.authTokensService.getAuthToken();
		return next
			.handle(this.addToken(req, token))
			.catch((err: any) => this.catchError(err, req, next));
	}

	private catchError(
		err: any,
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<any> {
		if (err.error) {
			// workaround to support old callbacks in AngularJS
			err = this.modifyErrorResponse(err);
		}

		if (err.status >= 200 && err.status < 300) {
			return Observable.of(this.handleErrorWithSuccessStatus(err));
		}

		if (err.status === 400) {
			return this.handle400Error(err, req, next);
		}

		if (err.status === 401) {
			return this.handle401Error(err, req, next);
		}

		// TODO remove after implementing Stripe v3
		// if (err.status === 403) {
		// 	return this.handle403Error();
		// }

		// this.handleUnprocessedError(err);

		return Observable.throw(err);
	}

	private handleErrorWithSuccessStatus(
		err: HttpErrorResponse
	): HttpResponse<any> {
		return new HttpResponse({
			body: null,
			headers: err.headers,
			status: err.status,
			statusText: err.statusText,
			url: err.url
		});
	}

	private handle400Error(
		err: HttpErrorResponse,
		req: HttpRequest<any>,
		next: HttpHandler
	) {
		if (err && err.error && err.error.errorCode === 450) {
			return this.logout();
		}
		return Observable.throw(err);
	}

	private handle401Error(
		err: HttpErrorResponse,
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<any> {
		const isInvalidAccessToken =
			err && err.error && err.error.errorCode === 452;
		const isInvalidRefreshToken =
			err && err.error && err.error.errorCode === 450;
		const isSubscriptionExpired =
			err && err.error && err.error.errorCode === 210;
		const hasNoAccessToTheCompany =
			err && err.error && err.error.errorCode === 453;

		if (isInvalidRefreshToken) {
			return this.logout();
		}

		if (isInvalidAccessToken) {
			if (!this.authTokensService.isRefreshingInProcess()) {
				// following requests must wait until the token comes back from the refreshToken call
				this.authTokensService.setRefreshingInProcess(true);
				this.authTokensService.emitTokenSubject(null);
				const refreshToken = this.authTokensService.getRefreshToken();
				if (!refreshToken) {
					return this.logout();
				}
				const logIn = localStorage.getItem('isStayLogin');
				if (logIn === null) {
					this.location.go('/login');
				}
				return this.accountService
					.getRefreshedAuthTokenWeb(refreshToken)
					.switchMap((tokensData: AccessTokens) => {
						if (!tokensData) {
							return this.logout();
						}
						const newRefreshToken = this.authTokensService.successGetRefreshedToken(
							tokensData
						);
						return Observable.of(newRefreshToken);
					})
					.switchMap((authToken: string) => {
						if (!authToken) {
							return this.logout();
						}
						this.authTokensService.emitTokenSubject(authToken);
						return next.handle(this.addToken(req, authToken));
					})
					.catch(error => this.catchError(error, req, next))
					.finally(() => {
						this.authTokensService.setRefreshingInProcess(false);
					});
			}
			return this.authTokensService.tokenSubject
				.filter(authToken => authToken !== null)
				.take(1)
				.switchMap(authToken => next.handle(this.addToken(req, authToken)));
		}

		if (isSubscriptionExpired) {
			return this.showPlans();
		}

		if (hasNoAccessToTheCompany) {
			this.location.go('/');
			return Observable.empty();
		}

		return this.logout();
	}

	private handle403Error() {
		if (!this.location.path().includes('subscription')) {
			this.location.go('subscription');
		}
		return Observable.empty();
	}

	private logout(): Observable<any> {
		this.accountService.successLogoutWeb();
		return Observable.empty();
	}

	private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
		if (this.authTokensService.isToken(req.url, token)) {
			req = req.clone({
				headers: req.headers.set('Authorization', `Bearer ${token}`)
			});
		}

		return req;
	}

	private modifyErrorResponse(err: HttpErrorResponse): HttpErrorResponse {
		const modifiedError = JSON.parse(JSON.stringify(err));
		try {
			const errorText = JSON.parse(modifiedError.error);
			modifiedError.data = {
				errorMessage: errorText.errorMessage
			};
		} catch (e) {
			if (modifiedError.error.errorMessage) {
				modifiedError.data = {
					errorMessage: err.error.errorMessage
				};
			} else {
				console.log('can not parse error');
			}
		}

		return modifiedError;
	}

	private showPlans(): Observable<void> {
		this.subscriptionService.onSubscriptionExpired();
		return Observable.empty();
	}

	private handleUnprocessedError(err): void {
		const { errorTitle, errorCode, errorMessage } = err.error;

		if (errorCode && errorMessage) {
			this.messageService.showErrorFromBackend(
				errorTitle || 'Whoops!',
				errorMessage
			);
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/webRequestsInterceptor.ts